// Removes query strings from span URLs before they reach Sentry.
//
// `dataCollection.urlQueryParams: false` strips the query from the URLs the
// SDK records itself (error request data, fetch spans, browser spans), but
// Next.js creates its own server span and stamps the full request target on
// it as `http.target`. The SDK passes that attribute through untouched, so a
// token or email address in a link would reach Sentry intact. This runs as
// `beforeSendSpan` in all three runtimes.
//
// Edge- and browser-safe: no Node imports.

const URL_ATTRIBUTES = ['http.target', 'http.url', 'url.full', 'url.path'];
const QUERY_ATTRIBUTES = ['url.query', 'http.query'];

/** `/a/b?x=1#y` -> `/a/b`. Strings with no `?` come back unchanged. */
export function stripQuery(value: string): string {
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
}

function stripAttribute(value: unknown): unknown {
  if (typeof value === 'string') return stripQuery(value);
  // Attributes can also arrive in their typed `{ value, type }` form.
  if (value && typeof value === 'object' && 'value' in value) {
    const typed = value as { value: unknown };
    if (typeof typed.value === 'string') return { ...typed, value: stripQuery(typed.value) };
  }
  return value;
}

type SpanWithAttributes = { name: string; attributes?: Record<string, unknown> };

export function stripSpanQuery<T extends SpanWithAttributes>(span: T): T {
  const attributes = span.attributes;
  if (attributes) {
    for (const key of URL_ATTRIBUTES) {
      if (key in attributes) attributes[key] = stripAttribute(attributes[key]);
    }
    for (const key of QUERY_ATTRIBUTES) delete attributes[key];
  }
  if (span.name.includes('?')) span.name = stripQuery(span.name);
  return span;
}
